import { isRoomOccupied } from '../../../services/guestRoomIcons';
import {
    getMonitorDayUseSplitBadgePlan,
    hasMonitorCrossReservationTurnover,
    MONITOR_BADGE_CHECKIN_COLOR,
    MONITOR_BADGE_CHECKOUT_COLOR
} from '../../../services/monitorBadgeDisplay';

export const MONITOR_DOOR_FOOTER_VARIANT_SOLID = 'solid';
export const MONITOR_DOOR_FOOTER_VARIANT_LIGHT = 'light';
export const MONITOR_DOOR_FOOTER_VARIANT_ACCENT = 'accent';

export const MONITOR_DOOR_CARD_V2_WIDTH_PX = 80;
export const MONITOR_DOOR_CARD_V2_MIN_HEIGHT_PX = 140;
export const MONITOR_DOOR_CARD_V2_BORDER_RADIUS_PX = 10;

export const MONITOR_DOOR_FOOTER_ICON_MIN_HEIGHT_PX = 24;
export const MONITOR_DOOR_FOOTER_BASE_BOTTOM_PX = -13;

/** Matches `.monitor-door-card-v2__res-header` min-height (border-box). */
export const MONITOR_BADGE_HEADER_HEIGHT_PX = 24;
/** Breathing room between door shell and progress / inspector band. */
export const MONITOR_BELOW_SHELL_GAP_PX = 14;
export const MONITOR_PROGRESS_GAP_ABOVE_PX = 4;
export const MONITOR_PROGRESS_BAR_HEIGHT_PX = 10;
export const MONITOR_CART_GAP_ABOVE_PX = 2;
/** Space below door footers before the next floor row (row separator band). */
export const MONITOR_DOOR_CARD_ROW_GAP_PX = 6;

/** Compact circles: 75% less vertical gap below cart / progress icons. */
export const COMPACT_MONITOR_BELOW_SHELL_GAP_PX = Math.round(MONITOR_BELOW_SHELL_GAP_PX * 0.25);
export const COMPACT_MONITOR_CART_GAP_ABOVE_PX = Math.round(MONITOR_CART_GAP_ABOVE_PX * 0.25);
export const COMPACT_MONITOR_CELL_MARGIN_BOTTOM_PX = Math.round(MONITOR_DOOR_CARD_ROW_GAP_PX * 0.25);
export const COMPACT_MONITOR_LABEL_ROW_HEIGHT_PX = Math.round(18 * 0.25);

/** Maximum stacked reservation headers on a door (turnover). */
export const MONITOR_BADGE_MAX_ROWS = 2;

function sortMonitorConfirmationBadges(list) {
    return [...list].sort((a, b) => {
        const aCheckin = a.checkin_today ? 1 : 0;
        const aCheckout = a.checkout_today ? 1 : 0;
        const bCheckin = b.checkin_today ? 1 : 0;
        const bCheckout = b.checkout_today ? 1 : 0;
        if (aCheckin !== bCheckin) return bCheckin - aCheckin;
        if (aCheckout !== bCheckout) return bCheckout - aCheckout;
        return 0;
    });
}

export function getMonitorConfirmationRows(data) {
    const raw =
        data?.confirmation_numbers ||
        (data?.confirmation_number
            ? [
                  {
                      confirmation_number: data.confirmation_number,
                      checkout_today: data.checkout_today || false,
                      checkin_today: data.checkin_today || false,
                      ...(data?.guest_last != null && data.guest_last !== ''
                          ? { guest_last: data.guest_last }
                          : {}),
                      ...(data?.guest_first != null && String(data.guest_first).trim() !== ''
                          ? { guest_first: data.guest_first }
                          : {})
                  }
              ]
            : []);
    return sortMonitorConfirmationBadges(raw);
}

/** Reservation header rows shown above the door shell (1 for day-use split). */
export function getMonitorDoorCardHeaderRowCount(data, badgeDisplayMode) {
    const confirmationRows = getMonitorConfirmationRows(data);
    const dayUsePlan = getMonitorDayUseSplitBadgePlan(confirmationRows, badgeDisplayMode);
    if (dayUsePlan) return 1;
    return confirmationRows.length;
}

/** Align all doors on a floor to the tallest badge stack in that row. */
export function getFloorMaxBadgeRowCount(rooms, badgeDisplayMode) {
    if (!rooms?.length) return 0;
    return rooms.reduce((max, room) => {
        const count = getMonitorDoorCardHeaderRowCount(room, badgeDisplayMode);
        return Math.max(max, count);
    }, 0);
}

export function hasMonitorProgressTiming(data) {
    const sec = Number(data?.function_seconds_allocated);
    return (
        data?.time_start != null &&
        String(data.time_start).trim() !== '' &&
        Number.isFinite(sec) &&
        sec > 0
    );
}

/**
 * Local QA: sample task progress + cart/inspector footers on monitor doors (door + compact layouts).
 * Set to `false` before shipping.
 */
export let USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS = false;

const DUMMY_MONITOR_PROGRESS_STARTED_MINUTES_AGO = 15;
const DUMMY_MONITOR_PROGRESS_SECONDS_ALLOCATED = 3600;

export function roomHasMonitorFooterActivity(data, show_icon = 0) {
    const apiRoleNameNorm = String(data?.role_name ?? '')
        .trim()
        .toLowerCase();
    const isRoomInspectorForMonitorFooter =
        apiRoleNameNorm === 'room inspector' || Boolean(data?.show_monitor_inspector_footer);
    const rawShowIcon = Number(data?.show_icon ?? show_icon ?? 0);
    const cartDisplayShowIcon = Number.isFinite(rawShowIcon) && rawShowIcon === 1 ? 1 : 0;

    return (
        Boolean(data?.cart_resource_initials) ||
        (isRoomInspectorForMonitorFooter && cartDisplayShowIcon === 1)
    );
}

/** When QA flag is on, rooms with cart/inspector footers get sample timing if the API omits it. */
export function getMonitorProgressTimingSource(data, show_icon = 0) {
    if (hasMonitorProgressTiming(data)) return data;
    if (!USE_DUMMY_MONITOR_FOOTER_AND_PROGRESS) return data;
    if (!roomHasMonitorFooterActivity(data, show_icon)) return data;

    const timeStart = new Date(
        Date.now() - DUMMY_MONITOR_PROGRESS_STARTED_MINUTES_AGO * 60 * 1000
    ).toISOString();

    return {
        ...data,
        time_start: timeStart,
        function_seconds_allocated: DUMMY_MONITOR_PROGRESS_SECONDS_ALLOCATED
    };
}

function resolveMonitorFooterLayoutFields(data, { isDnd = false, status, show_icon = 0 } = {}) {
    const timingData = getMonitorProgressTimingSource(data, show_icon);
    const showMonitorTaskProgressBar =
        hasMonitorProgressTiming(timingData) &&
        !(isDnd || status?.toLowerCase?.() === 'dnd');

    const apiRoleNameNorm = String(data?.role_name ?? '')
        .trim()
        .toLowerCase();
    const isRoomInspectorForMonitorFooter =
        apiRoleNameNorm === 'room inspector' || Boolean(data?.show_monitor_inspector_footer);

    const cartDisplayInitials = data?.cart_resource_initials;
    const rawShowIcon = Number(data?.show_icon ?? show_icon ?? 0);
    const cartDisplayShowIcon = Number.isFinite(rawShowIcon) && rawShowIcon === 1 ? 1 : 0;

    const monitorHasBottomFooter =
        Boolean(cartDisplayInitials) ||
        (isRoomInspectorForMonitorFooter && cartDisplayShowIcon === 1);

    return {
        timingData,
        showMonitorTaskProgressBar,
        isRoomInspectorForMonitorFooter,
        cartDisplayInitials,
        cartDisplayShowIcon,
        monitorHasBottomFooter
    };
}

export function getInitials(text) {
    if (!text) return '';
    const words = String(text).split(' ');
    return words.length > 1
        ? words[0][0].toUpperCase() + words[1][0].toUpperCase()
        : words[0][0].toUpperCase();
}

export function splitStatusDisplayLines(text) {
    if (text == null || String(text).trim() === '') return [];
    const raw = String(text).trim();
    // Keep common sentinel on one line (would otherwise split on "/").
    if (/^n\/a$/i.test(raw)) return [raw.toUpperCase()];

    const newlineParts = raw
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    if (newlineParts.length > 1) return newlineParts;

    const single = newlineParts[0] ?? raw;
    // Intentional breaks from API/comps use a spaced slash, e.g. "VACANT / DIRTY".
    if (/\s+\/\s+/.test(single)) {
        return single
            .split(/\s+\/\s+/)
            .map((s) => s.trim())
            .filter(Boolean);
    }

    return [single];
}

function parseHexRgb(color) {
    if (!color || typeof color !== 'string') return null;
    const v = color.trim();
    if (!v.startsWith('#')) return null;
    const hex = v.slice(1);
    if (hex.length === 3) {
        return [
            parseInt(hex[0] + hex[0], 16),
            parseInt(hex[1] + hex[1], 16),
            parseInt(hex[2] + hex[2], 16)
        ];
    }
    if (hex.length >= 6) {
        return [
            parseInt(hex.slice(0, 2), 16),
            parseInt(hex.slice(2, 4), 16),
            parseInt(hex.slice(4, 6), 16)
        ];
    }
    return null;
}

function relativeLuminance(color) {
    const rgb = parseHexRgb(color);
    if (!rgb) return null;
    const channels = rgb.map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function isLightColor(color, threshold = 0.8) {
    const lum = relativeLuminance(color);
    return lum != null && lum > threshold;
}

function isWhiteish(color) {
    return isLightColor(color, 0.93);
}

/** Multi-line footer labels to match door-card comps (e.g. VACANT / DIRTY, READY TO / OCCUPY). */
export function splitFooterLabelLines(text) {
    const fromApi = splitStatusDisplayLines(text);
    if (fromApi.length !== 1) return fromApi;
    const words = fromApi[0].split(/\s+/).filter(Boolean);
    if (words.length === 2) {
        return [words[0], words[1]];
    }
    if (words.length === 3) {
        return [`${words[0]} ${words[1]}`, words[2]];
    }
    if (words.length === 4) {
        return [`${words[0]} ${words[1]}`, `${words[2]} ${words[3]}`];
    }
    return fromApi;
}

/**
 * Footer layout variants (see design refs):
 * - accent: white panel, colored top rule + label (Ready to Occupy)
 * - light: tinted panel, colored label (Needs Inspection)
 * - solid: filled bar, light-on-dark text (Vacant Dirty, Out of Order, …)
 */
export function resolveMonitorDoorCardFooterStyle(data) {
    const textLines = splitFooterLabelLines(data?.status_display_text);
    const backgroundColor = data?.status_display_backcolor || '#343a40';
    const color = data?.status_display_forecolor || '#ffffff';
    const outer = data?.status_display_outer_border_color;
    const innerBorder = data?.status_display_inner_border_color;

    const backIsLight = isLightColor(backgroundColor, 0.82);
    const backIsWhite = isWhiteish(backgroundColor);
    const foreIsLight = isLightColor(color, 0.82);
    const accentColor =
        (!foreIsLight && color) || (outer && !isLightColor(outer, 0.82) && outer) || '#03A9F4';

    if (backIsWhite && !foreIsLight) {
        return {
            variant: MONITOR_DOOR_FOOTER_VARIANT_ACCENT,
            textLines,
            backgroundColor: '#ffffff',
            color: accentColor,
            borderTop: `2px solid ${accentColor}`
        };
    }

    if (backIsLight) {
        const ruleColor = innerBorder || outer || (!foreIsLight ? color : undefined) || '#6f42c1';
        return {
            variant: MONITOR_DOOR_FOOTER_VARIANT_LIGHT,
            textLines,
            backgroundColor,
            color: foreIsLight ? '#4a148c' : color,
            borderTop: `2px solid ${ruleColor}`
        };
    }

    return {
        variant: MONITOR_DOOR_FOOTER_VARIANT_SOLID,
        textLines,
        backgroundColor,
        color,
        borderTop: innerBorder ? `2px solid ${innerBorder}` : undefined
    };
}

export function getReservationHeaderPresentation(conf) {
    if (conf.checkout_today) {
        return {
            background: MONITOR_BADGE_CHECKOUT_COLOR,
            showCaret: true,
            caret: 'down',
            showDot: false
        };
    }
    if (conf.checkin_today) {
        return {
            background: MONITOR_BADGE_CHECKIN_COLOR,
            showCaret: true,
            caret: 'up',
            showDot: false
        };
    }
    return {
        background: '#06A9F4',
        showCaret: false,
        caret: null,
        showDot: true
    };
}

/**
 * Height of progress bar + cart/inspector band below the door shell (in-flow, not absolute).
 */
export function computeMonitorDoorBelowBlockHeightPx({
    data,
    isDnd = false,
    status,
    show_icon = 0,
    compact = false
}) {
    const belowShellGapPx = compact ? COMPACT_MONITOR_BELOW_SHELL_GAP_PX : MONITOR_BELOW_SHELL_GAP_PX;
    const cartGapAbovePx = compact ? COMPACT_MONITOR_CART_GAP_ABOVE_PX : MONITOR_CART_GAP_ABOVE_PX;
    const labelRowPx = compact ? COMPACT_MONITOR_LABEL_ROW_HEIGHT_PX : 18;

    const {
        showMonitorTaskProgressBar,
        monitorHasBottomFooter
    } = resolveMonitorFooterLayoutFields(data, { isDnd, status, show_icon });

    let belowBlockHeightPx = 0;
    const hasBelowContent =
        showMonitorTaskProgressBar || monitorHasBottomFooter || Boolean(data?.label_1);

    if (hasBelowContent) {
        belowBlockHeightPx += belowShellGapPx;
    }
    if (showMonitorTaskProgressBar) {
        belowBlockHeightPx += MONITOR_PROGRESS_BAR_HEIGHT_PX;
    }
    if (monitorHasBottomFooter) {
        belowBlockHeightPx +=
            (showMonitorTaskProgressBar ? cartGapAbovePx : 0) +
            MONITOR_DOOR_FOOTER_ICON_MIN_HEIGHT_PX;
    }
    if (data?.label_1) {
        belowBlockHeightPx += labelRowPx;
    }

    return belowBlockHeightPx;
}

/** Align footer icons / progress bars to the tallest below-shell band on the floor. */
export function getFloorMaxBelowBlockHeightPx(rooms, { compact = false } = {}) {
    if (!rooms?.length) return 0;
    return rooms.reduce((max, room) => {
        const height = computeMonitorDoorBelowBlockHeightPx({
            data: room,
            isDnd: room.isDnd,
            status: room.status,
            show_icon: room.show_icon,
            compact
        });
        return Math.max(max, height);
    }, 0);
}

/**
 * Mirror MonitorCard monitor-mode layout calculations for spacing, footers, and task bar.
 */
export function buildMonitorDoorCardLayoutState({
    data,
    isDnd,
    status,
    show_icon,
    isMonitor,
    badgeDisplayMode,
    compact = false
}) {
    const confirmationRows = getMonitorConfirmationRows(data);
    const dayUsePlan = getMonitorDayUseSplitBadgePlan(confirmationRows, badgeDisplayMode);
    const turnoverStacked =
        !dayUsePlan && hasMonitorCrossReservationTurnover(confirmationRows);
    const headerRowCount = dayUsePlan ? 1 : confirmationRows.length;

    const footerFields = resolveMonitorFooterLayoutFields(data, { isDnd, status, show_icon });

    const badgeStackHeightPx = headerRowCount * MONITOR_BADGE_HEADER_HEIGHT_PX;

    const belowBlockHeightPx = computeMonitorDoorBelowBlockHeightPx({
        data,
        isDnd,
        status,
        show_icon,
        compact
    });

    const shellMarginBottom = compact ? COMPACT_MONITOR_CELL_MARGIN_BOTTOM_PX : MONITOR_DOOR_CARD_ROW_GAP_PX;

    const isOutOfService =
        data?.service_status === 'out_of_service' || data?.service_status === 'out_of_order';

    return {
        confirmationRows,
        dayUsePlan,
        turnoverStacked,
        headerRowCount,
        progressTimingData: footerFields.timingData,
        showMonitorTaskProgressBar: footerFields.showMonitorTaskProgressBar,
        monitorHasBottomFooter: footerFields.monitorHasBottomFooter,
        isRoomInspectorForMonitorFooter: footerFields.isRoomInspectorForMonitorFooter,
        cartDisplayInitials: footerFields.cartDisplayInitials,
        cartDisplayShowIcon: footerFields.cartDisplayShowIcon,
        badgeStackHeightPx,
        belowBlockHeightPx,
        shellMarginBottom,
        isOutOfService,
        isOccupied: isRoomOccupied(data?.occupied)
    };
}
